import { useMemo } from 'react';

import { useAccountAddresses } from '../../../api';
import type {
  ChainAssetBalance,
  WalletSourceMetadata,
} from '../../../features/balances';
import { useUnifiedBalances } from '../../../features/balances';
import { calculateAssetDecimalPrecision } from '../../../utils';
import type { AssetSelection } from '../type';

export type SourceBalance = WalletSourceMetadata & {
  usdValue: number;
  tokenAmount: string;
  balance: ChainAssetBalance;
};

export function useAssetBalanceOfDifferentSources(asset: AssetSelection) {
  const { balances } = useUnifiedBalances({
    custody: asset.sources.map((s) => s.custody),
  });

  const addresses = useAccountAddresses({
    chain: [asset.chain],
  });

  const { sourceBalances, tokenSymbol } = useMemo(() => {
    const assetBalances = (balances || []).filter(
      (balance) =>
        balance.chain === asset.chain && balance.assetAddress === asset.address
    );

    if (assetBalances.length === 0) {
      return { sourceBalances: [], tokenSymbol: undefined };
    }

    const symbol = assetBalances[0].symbol;

    const balancesArray: SourceBalance[] = asset.sources
      .map((source) => {
        // use == here to compare with undefined and null, when it's undefined, it means it's a vault
        const accountAddress = addresses.find(
          (v) => v.walletId == source.walletId
        );
        if (!accountAddress) return null;

        const balance = assetBalances.find(
          (v) => v.walletAddress === accountAddress.address
        );
        if (!balance) return null;

        const assetDecimalPrecision = calculateAssetDecimalPrecision({
          tokenAmount: balance.decimalBalance.toString(),
          tokenDecimals: balance.decimals,
          rateUsd: balance.rateUsd ?? 0,
        });

        return {
          ...source,
          usdValue: balance.balanceUsd ?? 0,
          tokenAmount: balance.decimalBalance.toFixed(assetDecimalPrecision),
          balance,
        };
      })
      .filter((item): item is SourceBalance => item !== null)
      .sort((a, b) => b.usdValue - a.usdValue);

    return { sourceBalances: balancesArray, tokenSymbol: symbol };
  }, [balances, asset, addresses]);

  return { sourceBalances, tokenSymbol };
}
